import { useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Company } from '@marstein-radar/shared';
import { useAuth } from '../auth/AuthContext';
import { buttonStyle, inputStyle, personResultStyle } from '../styles';

interface WatchlistItem {
  entry: {
    id: string;
    companyId: string;
    note: string | null;
    groupLabel: string | null;
    createdAt: string;
    notifyNameChanged: boolean;
    notifyAddressChanged: boolean;
    notifyPersonAdded: boolean;
    notifyPersonRemoved: boolean;
  };
  company: Company;
}

const UNGROUPED_LABEL = 'Bez skupiny';

const ALERT_TYPE_LABELS: { key: keyof WatchlistItem['entry']; label: string }[] = [
  { key: 'notifyNameChanged', label: 'Zmena názvu' },
  { key: 'notifyAddressChanged', label: 'Zmena sídla' },
  { key: 'notifyPersonAdded', label: 'Nová osoba' },
  { key: 'notifyPersonRemoved', label: 'Odchod osoby' },
];

interface ApiKey {
  id: string;
  prefix: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

interface WatchlistInterest {
  id: string;
  label: string;
  region: string | null;
  eventType: string | null;
  keyword: string | null;
  matchCount: number;
}

const INTEREST_EVENT_TYPE_LABELS: Record<string, string> = {
  konkurz_restrukturalizacia: 'Konkurz/reštrukturalizácia',
  konkurz_vyrovnanie: 'Konkurz/vyrovnanie',
  likvidacia: 'Likvidácia',
  znizenie_imania: 'Zníženie základného imania',
};

// Týž. 21 — sledovanie insolvenčných udalostí podľa záujmu (kraj/typ/kľúčové slovo),
// nie podľa konkrétnej firmy. Uložený filter s okamžitým počtom zhôd (nie push alert —
// viď backend schema/watchlist.ts komentár pre dôvod, prečo zatiaľ nie). Vytváranie
// nového záujmu je na InsolvencyFeedPage (odkiaľ user filter už pozná), táto sekcia
// len zobrazuje/maže uložené záujmy, rovnaký vzor ako ApiKeysSection nižšie.
function WatchlistInterestsSection() {
  const qc = useQueryClient();

  const { data: interests } = useQuery<WatchlistInterest[]>({
    queryKey: ['watchlist-interests'],
    queryFn: async () => {
      const res = await fetch('/api/v1/watchlist/interests');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
  });

  async function remove(id: string): Promise<void> {
    await fetch(`/api/v1/watchlist/interests/${id}`, { method: 'DELETE' });
    await qc.invalidateQueries({ queryKey: ['watchlist-interests'] });
  }

  return (
    <section>
      <div className="section-title">
        <h2>Sledované záujmy</h2>
      </div>
      <div
        className="card"
        style={{ padding: interests && interests.length > 0 ? '0' : '20px 22px' }}
      >
        <p className="hint" style={{ display: 'block', padding: '14px 22px 0' }}>
          Uložené filtre insolvenčných udalostí (kraj, typ, kľúčové slovo) — pridáš ich priamo na{' '}
          <Link to="/insolvencie">stránke Insolvencie</Link>. Zobrazuje aktuálny počet zhôd; ide o
          uložené hľadanie, nie o priebežné upozornenia na nové udalosti.
        </p>
        {(!interests || interests.length === 0) && (
          <p style={{ color: 'var(--slate)', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
            Zatiaľ žiadne uložené záujmy.
          </p>
        )}
        {interests && interests.length > 0 && (
          <ul className="list-plain" style={{ margin: '14px 0 0' }}>
            {interests.map((i) => {
              const params = new URLSearchParams();
              if (i.region) params.set('region', i.region);
              if (i.eventType) params.set('eventType', i.eventType);
              return (
                <li
                  key={i.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 22px',
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  <div>
                    <b>{i.label}</b>
                    <div className="hint" style={{ display: 'block' }}>
                      {[
                        i.region,
                        i.eventType
                          ? (INTEREST_EVENT_TYPE_LABELS[i.eventType] ?? i.eventType)
                          : null,
                        i.keyword ? `„${i.keyword}"` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Všetky udalosti'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Link to={`/insolvencie?${params.toString()}`} className="pill mid">
                      {i.matchCount} zhôd
                    </Link>
                    <button
                      type="button"
                      className="btn-link"
                      style={{ fontSize: '0.85rem' }}
                      onClick={() => void remove(i.id)}
                    >
                      Odstrániť
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

// Blok 4, Týž. 16 — self-service API kľúče. Plný kľúč sa vráti len raz pri vytvorení
// (rovnaký vzor ako GitHub PAT) — zobrazí sa v UI, kým sa stránka neopustí, ďalej sa
// dá už len rozoznať podľa `prefix`.
function ApiKeysSection() {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: keys } = useQuery<ApiKey[]>({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const res = await fetch('/api/v1/auth/api-keys');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
  });

  async function createKey(): Promise<void> {
    setError(null);
    setCreating(true);
    try {
      const res = await fetch('/api/v1/auth/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim() || undefined }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message ?? `${res.status} ${res.statusText}`);
      setFreshKey(body.key);
      setLabel('');
      await qc.invalidateQueries({ queryKey: ['api-keys'] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function revokeKey(id: string): Promise<void> {
    await fetch(`/api/v1/auth/api-keys/${id}`, { method: 'DELETE' });
    await qc.invalidateQueries({ queryKey: ['api-keys'] });
  }

  const active = keys?.filter((k) => !k.revokedAt) ?? [];

  return (
    <section>
      <div className="section-title">
        <h2>API kľúče</h2>
        <Link to="/dokumentacia-api" className="hint">
          Dokumentácia →
        </Link>
      </div>
      <div className="card">
        <p style={{ color: 'var(--slate)', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          Programatický prístup k rovnakým dátam ako vo webe (profil, financie, risk skóre, graf
          prepojení) — zadarmo, s denným limitom podľa plánu (free 200 / deň, pro 10 000 / deň).
        </p>

        {freshKey && (
          <div
            style={{
              background: 'var(--risk-low-soft)',
              border: '1px solid var(--risk-low)',
              borderRadius: 8,
              padding: '0.6rem 0.75rem',
              marginBottom: '0.75rem',
              fontSize: '0.82rem',
            }}
          >
            <p style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>
              Kľúč vytvorený — ulož si ho, znova sa už nezobrazí:
            </p>
            <code style={{ wordBreak: 'break-all', userSelect: 'all' }}>{freshKey}</code>
            <div>
              <button
                type="button"
                className="btn-link"
                style={{ fontSize: '0.78rem', marginTop: '0.35rem' }}
                onClick={() => setFreshKey(null)}
              >
                Skryť
              </button>
            </div>
          </div>
        )}

        {active.length === 0 && <p className="list-plain empty">Zatiaľ žiadny API kľúč.</p>}
        {active.length > 0 && (
          <ul className="list-plain" style={{ marginBottom: '0.75rem' }}>
            {active.map((k) => (
              <li
                key={k.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0',
                }}
              >
                <span>
                  <code style={{ fontSize: '0.82rem' }}>{k.prefix}…</code>
                  {k.label && (
                    <span style={{ color: 'var(--slate)', fontSize: '0.8rem' }}> · {k.label}</span>
                  )}
                  {k.lastUsedAt && (
                    <span style={{ color: 'var(--slate-light)', fontSize: '0.75rem' }}>
                      {' '}
                      · naposledy použitý {new Date(k.lastUsedAt).toLocaleDateString('sk-SK')}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="btn-link"
                  style={{ fontSize: '0.78rem' }}
                  onClick={() => void revokeKey(k.id)}
                >
                  Zrušiť
                </button>
              </li>
            ))}
          </ul>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Popis kľúča (voliteľné)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ ...inputStyle, flex: 1, margin: 0 }}
          />
          <button
            type="button"
            onClick={() => void createKey()}
            disabled={creating}
            style={buttonStyle}
          >
            {creating ? 'Vytváram...' : '+ Nový kľúč'}
          </button>
        </div>
        {error && (
          <p style={{ color: 'var(--risk-high)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

// /ucet — Blok 4, Týž. 14. Plán, "Prejsť na Pro" (Stripe checkout), zoznam sledovaných firiem.
export function AccountPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [upgrading, setUpgrading] = useState(false);

  const { data: watchlist } = useQuery<WatchlistItem[]>({
    queryKey: ['watchlist'],
    queryFn: async () => {
      const res = await fetch('/api/v1/watchlist');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!user,
  });

  // Týž. 20+ — voľné zoskupovanie sledovaných firiem (finstat inšpirácia: portfólio
  // rozdelené na dodávateľov/odberateľov/konkurenciu, ale bez pevnej štruktúry —
  // len vlastný textový štítok). Autocomplete z predtým použitých štítkov.
  const { data: groupLabels } = useQuery<string[]>({
    queryKey: ['watchlist-group-labels'],
    queryFn: async () => {
      const res = await fetch('/api/v1/watchlist/group-labels');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!user,
  });

  async function setGroupLabel(ico: string, groupLabel: string): Promise<void> {
    await fetch(`/api/v1/companies/${ico}/watch`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ groupLabel: groupLabel.trim() || null }),
    });
    await qc.invalidateQueries({ queryKey: ['watchlist'] });
    await qc.invalidateQueries({ queryKey: ['watchlist-group-labels'] });
  }

  const grouped = new Map<string, WatchlistItem[]>();
  for (const item of watchlist ?? []) {
    const label = item.entry.groupLabel || UNGROUPED_LABEL;
    const arr = grouped.get(label) ?? [];
    arr.push(item);
    grouped.set(label, arr);
  }
  // Pomenované skupiny prvé (abecedne), "Bez skupiny" vždy posledné.
  const groupOrder = [...grouped.keys()]
    .filter((k) => k !== UNGROUPED_LABEL)
    .sort()
    .concat(grouped.has(UNGROUPED_LABEL) ? [UNGROUPED_LABEL] : []);

  // Týž. 19 — per-alert-type notifikačné preferencie (napr. vypnúť len upozornenia
  // na zmenu sídla). Optimistic-ish: refetch watchlist po úspešnom PATCH.
  async function togglePreference(
    ico: string,
    key: keyof WatchlistItem['entry'],
    value: boolean,
  ): Promise<void> {
    await fetch(`/api/v1/companies/${ico}/watch`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ [key]: value }),
    });
    await qc.invalidateQueries({ queryKey: ['watchlist'] });
  }

  if (loading) return null;
  if (!user) return <Navigate to="/prihlasenie" replace />;

  async function upgrade(): Promise<void> {
    setUpgradeError(null);
    setUpgrading(true);
    try {
      const res = await fetch('/api/v1/billing/checkout', { method: 'POST' });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message ?? `${res.status} ${res.statusText}`);
      window.location.href = body.url;
    } catch (err) {
      setUpgradeError((err as Error).message);
    } finally {
      setUpgrading(false);
    }
  }

  return (
    <main className="page page-narrow">
      <div className="page-header">
        <h1>Účet</h1>
        <p>{user.email}</p>
      </div>

      <section>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span className={`pill ${user.plan === 'pro' ? 'low' : 'mid'}`}>
              {user.plan === 'pro' ? 'Pro plán' : 'Free plán'}
            </span>
            {user.plan !== 'pro' && (
              <button type="button" onClick={upgrade} disabled={upgrading} style={buttonStyle}>
                {upgrading ? 'Presmerovávam...' : 'Prejsť na Pro'}
              </button>
            )}
          </div>
          <p style={{ color: 'var(--slate)', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
            Dáta (profil, financie, graf prepojení, risk skóre) sú zadarmo pre všetkých — bez
            limitu. Free plán obmedzuje len počet sledovaných firiem (watchlist, max 5). Pro plán:
            neobmedzené sledovanie + prioritné alerty.
          </p>
          {upgradeError && (
            <p style={{ color: 'var(--risk-high)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
              {upgradeError}
            </p>
          )}
        </div>
      </section>

      <section>
        <div className="section-title">
          <h2>Sledované firmy</h2>
          {watchlist && watchlist.length > 0 && (
            <span className="hint">
              {watchlist.length} / {user.plan === 'pro' ? '∞' : 5}
            </span>
          )}
        </div>
        <div
          className="card"
          style={{ padding: watchlist && watchlist.length > 0 ? '0 22px' : '20px 22px' }}
        >
          {watchlist && watchlist.length === 0 && (
            <p className="list-plain empty">Zatiaľ nesleduješ žiadnu firmu.</p>
          )}
          {watchlist && watchlist.length > 0 && (
            <>
              <datalist id="watchlist-group-labels">
                {groupLabels?.map((l) => (
                  <option key={l} value={l} />
                ))}
              </datalist>
              {groupOrder.map((groupName) => (
                <div key={groupName} style={{ marginBottom: '0.5rem' }}>
                  <div
                    style={{
                      fontSize: '0.76rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: 'var(--slate)',
                      padding: '10px 0 4px',
                    }}
                  >
                    {groupName} <span className="hint">({grouped.get(groupName)!.length})</span>
                  </div>
                  <ul className="list-plain">
                    {grouped.get(groupName)!.map((w) => (
                      <li key={w.entry.id} style={{ padding: '10px 0' }}>
                        <button
                          type="button"
                          onClick={() => navigate(`/firma/${w.company.ico}`)}
                          style={{ ...personResultStyle, padding: 0, borderBottom: 'none' }}
                        >
                          {w.company.name ?? w.company.ico}
                          <span
                            className="num"
                            style={{ color: 'var(--slate)', fontSize: '0.8rem' }}
                          >
                            {' '}
                            · IČO {w.company.ico}
                          </span>
                        </button>
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.9rem',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            marginTop: '0.4rem',
                            fontSize: '0.78rem',
                            color: 'var(--slate)',
                          }}
                        >
                          {ALERT_TYPE_LABELS.map(({ key, label }) => (
                            <label
                              key={key}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={w.entry[key] as boolean}
                                onChange={(e) =>
                                  void togglePreference(w.company.ico, key, e.target.checked)
                                }
                              />
                              {label}
                            </label>
                          ))}
                          <label
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                          >
                            Skupina:
                            <input
                              type="text"
                              list="watchlist-group-labels"
                              defaultValue={w.entry.groupLabel ?? ''}
                              placeholder="napr. Odberatelia"
                              style={{
                                ...inputStyle,
                                margin: 0,
                                padding: '0.2rem 0.5rem',
                                width: 140,
                              }}
                              onBlur={(e) => {
                                if (e.target.value !== (w.entry.groupLabel ?? '')) {
                                  void setGroupLabel(w.company.ico, e.target.value);
                                }
                              }}
                            />
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      <WatchlistInterestsSection />

      <ApiKeysSection />

      <button
        type="button"
        onClick={() => void logout()}
        className="btn-link"
        style={{ fontSize: '0.85rem' }}
      >
        Odhlásiť sa
      </button>
    </main>
  );
}
